import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useFormularioDinamico } from '../../../../hooks/useFormularioDinamico';

function FirmaPad({ onSave, onClear }) {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = canvas.offsetWidth * 2;
    canvas.height = canvas.offsetHeight * 2;
    const ctx = canvas.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#1e293b";
  }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDrawing = (e) => {
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  const handleClear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    onClear();
  };

  return (
    <div className="flex flex-col items-center gap-3 w-full my-4">
      <div className="bg-white/60 backdrop-blur-md border border-slate-300 rounded-2xl shadow-inner overflow-hidden w-full max-w-[400px] h-[180px] relative touch-none">
        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
        />
        <div className="absolute top-3 left-4 text-[10px] font-black text-slate-400 uppercase tracking-widest pointer-events-none select-none">
          Firma del Responsable
        </div>
      </div>
      <button 
        onClick={handleClear}
        type="button"
        className="text-[10px] uppercase tracking-wider font-black text-slate-500 hover:text-red-500 transition-colors cursor-pointer bg-white/50 px-4 py-1.5 rounded-full border border-slate-200 shadow-sm"
      >
        Limpiar Firma
      </button>
    </div>
  );
}

function SafePortalModal({ children }) {
  const elRef = useRef(null);
  if (!elRef.current && typeof document !== "undefined") {
    const div = document.createElement("div");
    div.setAttribute("data-portal-modal", "true");
    div.setAttribute("translate", "no");
    elRef.current = div;
  }
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    document.body.appendChild(el);
    return () => { if (el.parentNode) el.parentNode.removeChild(el); };
  }, []);
  if (!elRef.current) return null;
  return ReactDOM.createPortal(children, elRef.current);
}

const NOMBRES_MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

export default function ManejoResiduosSST({ usuario, onBack }) {
  // Conectamos con el motor lógico y ABAC
  const {
    formulario, respuestas: datosServer, loading, enviando, sedes,
    handleChange: handleServerChange, handleSubmit
  } = useFormularioDinamico('manejo-residuos', usuario);

  const [customAlert, setCustomAlert] = useState({ show: false, message: "", type: "success" });
  const [registrosMesLocal, setRegistrosMesLocal] = useState([]);

  const nombreUsuario = usuario?.nombres || usuario?.nombre || '';

  // Valores iniciales
  useEffect(() => {
    if (!datosServer.fecha) handleServerChange('fecha', new Date().toISOString().split('T')[0]);
    if (!datosServer.responsable && nombreUsuario) handleServerChange('responsable', nombreUsuario);
    if (!datosServer.entregado_a) handleServerChange('entregado_a', 'CENTRO DE ACOPIO');
  }, [datosServer.fecha, datosServer.responsable, datosServer.entregado_a, nombreUsuario, handleServerChange]);

  // Extraer opciones de residuos parametrizadas desde la BD
  const campoResiduos = formulario?.campos?.find(c => c.nombre_campo === 'residuos_generados');
  const tiposResiduos = Array.isArray(campoResiduos?.opciones) ? campoResiduos.opciones : [];

  const residuosGenerados = datosServer.residuos_array || [];

  const obtenerHeadersConAbac = useCallback(() => {
    const token = localStorage.getItem("token");
    const userData = localStorage.getItem("user_data") || localStorage.getItem("userData");
    const abacPerms = localStorage.getItem("abac_permissions") || localStorage.getItem("abacPermissions");

    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(userData && { "x-user-data": userData }),
      ...(abacPerms && { "x-abac-permissions": abacPerms })
    };
  }, []);

  const cargarRegistrosDelMes = useCallback(async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const res = await fetch(`${API_URL}/api/formularios/formularios/historial/MANEJO_RESIDUOS_SST`, {
        headers: obtenerHeadersConAbac()
      });
      const data = await res.json();
      if (data.success) {
        setRegistrosMesLocal(data.data || []);
      }
    } catch (e) {
      console.error("Error al cargar historial:", e);
    }
  }, [obtenerHeadersConAbac]);

  useEffect(() => {
    cargarRegistrosDelMes();
  }, [cargarRegistrosDelMes]);

  const handleCheckbox = (residuo) => {
    const actual = [...residuosGenerados];
    const nuevoArray = actual.includes(residuo) ? actual.filter(r => r !== residuo) : [...actual, residuo];
    handleServerChange('residuos_array', nuevoArray);
    handleServerChange('residuos_generados', nuevoArray.join(', '));
  };

  const guardarLocal = (e) => {
    if (!datosServer.pdv || !String(datosServer.pdv).trim()) {
      setCustomAlert({ show: true, message: "Selecciona una Sede (PDV) válida de la lista.", type: "warning" });
      return;
    }
    if (residuosGenerados.length === 0) {
      setCustomAlert({ show: true, message: "Selecciona al menos un residuo.", type: "warning" });
      return;
    }
    if (!datosServer.cantidad_kg || isNaN(datosServer.cantidad_kg) || Number(datosServer.cantidad_kg) <= 0) {
      setCustomAlert({ show: true, message: "Ingresa una cantidad en KG.", type: "warning" });
      return;
    }
    if (!datosServer.firma_responsable) {
      setCustomAlert({ show: true, message: "Tu firma es obligatoria.", type: "warning" });
      return;
    }

    handleSubmit(e, () => {
      setCustomAlert({ show: true, message: "¡Registro guardado exitosamente!", type: "success" });
      cargarRegistrosDelMes(); 
      setTimeout(() => { 
        onBack && onBack(); 
      }, 1500);
    });
  };

  const renderTablaEnVivo = () => {
    if (!datosServer.pdv || !String(datosServer.pdv).trim()) {
      return (
        <div className="text-center p-8 bg-emerald-50 rounded-xl border border-emerald-100 shadow-inner">
          <span className="text-3xl mb-2 block opacity-50">📍</span>
          <p className="text-sm font-bold text-emerald-800">Selecciona una sede en la parte superior para cargar la tabla mensual automáticamente.</p>
        </div>
      );
    }

    const fechaActual = datosServer.fecha || new Date().toISOString().split('T')[0];
    const [anio, mes] = fechaActual.split('-');
    const nombreMes = NOMBRES_MESES[parseInt(mes, 10) - 1];
    const diasDelMes = new Date(anio, mes, 0).getDate();
    
    const sedeSeleccionadaLimpia = String(datosServer.pdv).trim().toLowerCase();
    
    const registrosSede = registrosMesLocal.filter(r => {
      const rPdv = (r.pdv_centro || "").trim().toLowerCase();
      const rFecha = (r.fecha || "").split('T')[0];
      return rPdv === sedeSeleccionadaLimpia && rFecha.startsWith(`${anio}-${mes}`);
    });

    const filas = [];
    let sumaTotalKg = 0;

    for (let i = 1; i <= diasDelMes; i++) {
      const fechaBuscada = `${anio}-${mes}-${String(i).padStart(2, '0')}`;
      const registroDia = registrosSede.find(r => {
        const rFechaLimpia = (r.fecha || "").split('T')[0];
        return rFechaLimpia === fechaBuscada;
      });
      
      let papel='', carton='', plastico='', vidrio='', latas='', noaprovechable='', organico='', otros='', cantidad='', entregado='';
      
      if (registroDia) {
        let resp = {};
        if (typeof registroDia.respuestas === 'string') {
          try { resp = JSON.parse(registroDia.respuestas); } catch(e){}
        } else { resp = registroDia.respuestas || {}; }
        
        const rStr = resp.residuos_generados ? String(resp.residuos_generados).toUpperCase() : "";
        if (rStr.includes("PAPEL")) papel = 'X';
        if (rStr.includes("CARTON")) carton = 'X';
        if (rStr.includes("PLASTICO")) plastico = 'X';
        if (rStr.includes("VIDRIO")) vidrio = 'X';
        if (rStr.includes("LATAS")) latas = 'X';
        if (rStr.includes("NO_APROVECHABLE")) noaprovechable = 'X';
        if (rStr.includes("ORGANICO")) organico = 'X';
        if (rStr.includes("OTROS")) otros = 'X';
        
        cantidad = Number(resp.cantidad_kg) || 0;
        sumaTotalKg += cantidad;
        entregado = resp.entregado_a || '';
      }

      filas.push(
        <tr key={i} className={`text-center border-b border-slate-200 ${registroDia ? 'bg-emerald-100/50 font-bold' : 'bg-white'}`}>
          <td className="p-1 font-bold text-slate-700 text-[10px]">{i}</td>
          <td className="p-1 text-emerald-600 font-black text-[10px]">{papel}</td>
          <td className="p-1 text-emerald-600 font-black text-[10px]">{carton}</td>
          <td className="p-1 text-emerald-600 font-black text-[10px]">{plastico}</td>
          <td className="p-1 text-emerald-600 font-black text-[10px]">{vidrio}</td>
          <td className="p-1 text-emerald-600 font-black text-[10px]">{latas}</td>
          <td className="p-1 text-emerald-600 font-black text-[10px]">{noaprovechable}</td>
          <td className="p-1 text-emerald-600 font-black text-[10px]">{organico}</td>
          <td className="p-1 text-emerald-600 font-black text-[10px]">{otros}</td>
          <td className="p-1 font-black text-slate-800 text-[10px]">{cantidad ? cantidad : ''}</td>
          <td className="p-1 text-[8px] text-slate-600 font-bold uppercase truncate max-w-[60px]">{entregado}</td>
        </tr>
      );
    }

    return (
      <div className="bg-white/80 p-4 rounded-2xl shadow-sm border border-emerald-200 mt-6 overflow-hidden">
        <h3 className="text-xs font-black text-emerald-800 uppercase tracking-widest mb-3 flex items-center justify-between">
          <span>Vista Previa de la Tabla del Mes</span>
          <span className="bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-md">{nombreMes} {anio}</span>
        </h3>
        <div className="overflow-x-auto rounded-xl border border-slate-200 shadow-sm max-h-[300px]">
          <table className="w-full text-left border-collapse min-w-[500px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-emerald-600 text-white text-[8px] uppercase tracking-wider text-center">
                <th className="p-1.5 border-r border-emerald-500/50" rowSpan="2">Día</th>
                <th className="p-1.5 border-r border-b border-emerald-500/50" colSpan="8">Tipo de Residuo</th>
                <th className="p-1.5 border-r border-emerald-500/50" rowSpan="2">Cant (KG)</th>
                <th className="p-1.5" rowSpan="2">Entregado</th>
              </tr>
              <tr className="bg-emerald-700 text-white text-[8px] uppercase tracking-wider text-center">
                <th className="p-1 border-r border-emerald-500/50">Papel</th>
                <th className="p-1 border-r border-emerald-500/50">Cart.</th>
                <th className="p-1 border-r border-emerald-500/50">Plast.</th>
                <th className="p-1 border-r border-emerald-500/50">Vidr.</th>
                <th className="p-1 border-r border-emerald-500/50">Lata</th>
                <th className="p-1 border-r border-emerald-500/50">No Aprov.</th>
                <th className="p-1 border-r border-emerald-500/50">Orgán.</th>
                <th className="p-1 border-r border-emerald-500/50">Otro</th>
              </tr>
            </thead>
            <tbody>
              {filas}
              <tr className="bg-slate-100 font-black text-slate-800 text-center border-t-2 border-emerald-600 sticky bottom-0">
                <td className="p-1.5 border-r border-slate-200 text-[10px]" colSpan="9">TOTAL MENSUAL</td>
                <td className="p-1.5 border-r border-slate-200 text-emerald-700 text-xs">{sumaTotalKg.toFixed(1)} KG</td>
                <td className="p-1.5"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (loading || !formulario) return <div className="p-10 text-center text-white font-bold">Cargando Manejo de Residuos...</div>;

  return (
    <div className="bg-white/20 backdrop-blur-3xl rounded-2xl sm:rounded-[34px] shadow-2xl max-w-4xl w-full mx-auto border border-white/30 animate-fade-in relative h-[90vh] overflow-hidden flex flex-col">
      
      <div className="bg-transparent border-b border-white/20 px-4 py-3 sm:px-8 sm:py-5 z-40 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="w-10 h-10 flex-none bg-white/60 hover:bg-white/80 text-slate-700 hover:text-red-500 flex items-center justify-center rounded-full transition-all shadow-sm border border-white/40 cursor-pointer">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-black text-slate-800 leading-tight">{formulario.titulo}</h1>
            <span className="bg-emerald-500/20 text-emerald-800 border border-emerald-500/30 text-[10px] font-bold px-2 py-0.5 rounded-md backdrop-blur-sm">FT-SST-09 | Registro Diario</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-8 space-y-5 relative custom-scrollbar">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="space-y-5">
            <div className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60">
              <h2 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-4">📋 Datos del PDV</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-black text-slate-700 mb-1 uppercase">PDV / Sede *</label>
                  <select 
                    name="pdv" 
                    value={datosServer.pdv || ""} 
                    onChange={(e) => handleServerChange('pdv', e.target.value)} 
                    className="w-full bg-white/80 border-white focus:border-emerald-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm cursor-pointer"
                  >
                    <option value="" disabled>-- Selecciona una Sede --</option>
                    {sedes.map(sede => (
                      <option key={sede.id} value={sede.nombre}>
                        {sede.nombre} {sede.codigo_bodega ? `(Bodega: ${sede.codigo_bodega})` : ""}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-black text-slate-700 mb-1 uppercase">Fecha *</label>
                    <input type="date" name="fecha" value={datosServer.fecha || ""} onChange={(e) => handleServerChange('fecha', e.target.value)} className="w-full bg-white/80 border-white focus:border-emerald-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-700 mb-1 uppercase">Cant (KG) *</label>
                    <input type="number" step="0.1" min="0" name="cantidad_kg" value={datosServer.cantidad_kg || ""} onChange={(e) => handleServerChange('cantidad_kg', e.target.value)} placeholder="Ej: 10.5" className="w-full bg-white/80 border-white focus:border-emerald-400 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 outline-none shadow-sm" />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60">
              <h2 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-3">♻️ Residuo Generado</h2>
              <div className="grid grid-cols-3 gap-2">
                {tiposResiduos.map((residuo) => (
                  <label key={residuo} className={`flex items-center justify-center p-2 rounded-xl border-2 cursor-pointer transition-all ${residuosGenerados.includes(residuo) ? 'bg-emerald-100 border-emerald-400 text-emerald-800' : 'bg-white/60 border-white/60 text-slate-500 hover:bg-white'}`}>
                    <input type="checkbox" checked={residuosGenerados.includes(residuo)} onChange={() => handleCheckbox(residuo)} className="hidden" />
                    <span className="text-[9px] font-black">{residuo.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="bg-white/50 backdrop-blur-xl p-5 rounded-[24px] shadow-sm border border-white/60 flex flex-col items-center">
              <h3 className="text-sm font-black text-slate-800 uppercase tracking-widest mb-1">Cierre Diario</h3>
              <p className="text-[10px] text-slate-600 font-bold mb-2">Firma del empleado</p>
              
              <FirmaPad onSave={(firma) => handleServerChange('firma_responsable', firma)} onClear={() => handleServerChange('firma_responsable', null)} />

              <button onClick={guardarLocal} disabled={enviando} className="w-full mt-4 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-white font-black py-3 rounded-xl shadow-lg active:scale-95 transition-all text-xs tracking-widest uppercase cursor-pointer border border-emerald-300 disabled:opacity-50">
                {enviando ? 'Guardando...' : 'Registrar Entrega'}
              </button>
            </div>
          </div>

        </div>

        {renderTablaEnVivo()}

      </div>

      {customAlert.show && (
        <SafePortalModal>
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md flex justify-center items-center p-4 z-[99999] animate-fade-in">
            <div className="bg-white/50 backdrop-blur-3xl rounded-[32px] p-6 sm:p-8 max-w-sm w-full text-center shadow-[0_30px_90px_rgba(0,0,0,0.2)] border border-white/80">
              <div className={`w-20 h-20 mx-auto rounded-3xl flex items-center justify-center mb-5 shadow-sm border border-white/80 ${customAlert.type === 'success' ? 'bg-emerald-500/20 text-emerald-700' : customAlert.type === 'warning' ? 'bg-amber-500/20 text-amber-700' : 'bg-red-500/20 text-red-700'}`}>
                <span className="text-4xl">{customAlert.type === 'success' ? '✅' : customAlert.type === 'warning' ? '⚠️' : '❌'}</span>
              </div>
              <h3 className="text-xl font-extrabold text-slate-800 mb-2.5">{customAlert.type === 'success' ? '¡Éxito!' : 'Atención'}</h3>
              <p className="text-sm text-slate-700 font-medium mb-8 leading-relaxed px-2">{customAlert.message}</p>
              <button onClick={() => setCustomAlert({ ...customAlert, show: false })} className={`w-full py-3.5 rounded-2xl font-bold text-white transition-all shadow-lg cursor-pointer ${customAlert.type === 'success' ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-500 hover:bg-amber-400'}`}>Entendido</button>
            </div>
          </div>
        </SafePortalModal>
      )}
    </div>
  );
}